import axios from 'axios';

import * as errorHandlerActions from '@/components/errorHandler/Actions';
import {
	ACCESS_TOKEN_EXPIRATION_DAYS,
	AUTH_URL,
	COOKIES,
	IS_ADMIN,
	LOGOUT_REDIRECT_URL,
	ROUTES_API,
} from '@/services/Constants';
import {
	getCookie,
	getLocalStorageItem,
	isSecure,
	removeCookie,
	setCookie,
	setLocalStorageItem,
} from '@/services/Helper';
import { processRequest } from '@/services/Api';

export const getAccessToken = (): string | undefined => {
	const token: string | undefined = getCookie(COOKIES.accessToken);
	const temporaryToken: string | undefined = getCookie(COOKIES.temporaryToken);
	return token || temporaryToken;
};

export const refreshAccessToken = async (): Promise<void> => {
	try {
		if (IS_ADMIN) {
			const { data }: { data: { auth_token: string } } = await processRequest(
				'auth/refresh_token',
				'POST',
			);
			const { auth_token } = data || {};
			await setAccessToken(auth_token);
		} else {
			// @ts-ignore
			const { data } = await axios({
				headers: { 'Content-Type': 'application/json' },
				method: 'GET',
				params: { client_id: 'learning_path@squla', response_type: 'token' },
				crossDomain: true,
				mode: 'cors',
				url: AUTH_URL,
			});
			const { access_token, expires_in } = data || {};
			if (!access_token) {
				throw new Error('No access token returned from auth server');
			}
			await setAccessToken(access_token, expires_in);
		}
	} catch (e) {
		removeAccessToken();
		window.location.href = LOGOUT_REDIRECT_URL;
	}
};

export const setAccessToken = (accessToken: string, expiresIn?: number): void => {
	const cookieOptions = {
		expires: ACCESS_TOKEN_EXPIRATION_DAYS,
		secure: isSecure(),
	};
	setCookie(COOKIES.accessToken, accessToken, cookieOptions);
	if (expiresIn) {
		const expiresAt: number = Math.floor(Date.now() / 1000) + expiresIn;
		setCookie(COOKIES.tokenValidUntil, expiresAt, cookieOptions);
	}
};

export const setTemporaryAccessToken = (token: string): void => {
	setCookie(COOKIES.temporaryToken, token, {
		expires: ACCESS_TOKEN_EXPIRATION_DAYS,
		secure: isSecure(),
	});
};

export const removeAccessToken = (): void => {
	removeCookie(COOKIES.secureSession, { path: '/', secure: true });
	removeCookie(COOKIES.accessToken);
	removeCookie(COOKIES.tokenValidUntil);
};

export const removeTemporaryAccessToken = (): void => {
	removeCookie(COOKIES.temporaryToken);
};

export const isAuthenticated = (): boolean => {
	return !!getCookie(COOKIES.accessToken);
};

export const getTokenData = (): Record<string, unknown> | null => {
	try {
		const token: string | undefined = getCookie(COOKIES.accessToken);
		if (!token) return null;
		const tokenPayload: string = token.split('.')[1];
		const decodedTokenPayload: string = atob(tokenPayload);
		return JSON.parse(decodedTokenPayload);
	} catch (e) {
		if (e instanceof TypeError || e instanceof SyntaxError) {
			return null;
		}
		throw e;
	}
};

export const checkExpiredAccessToken = (): Promise<void> => {
	const timestamp: string | undefined = getCookie(COOKIES.tokenValidUntil);
	if (!timestamp) return Promise.resolve();
	const expiryDate: Date = new Date(Number(timestamp) * 1000);
	const now: Date = new Date();
	if (now > expiryDate) {
		return refreshAccessToken();
	}
	return Promise.resolve();
};

export const setAITutorAccessToken = (accessToken: string): void => {
	setLocalStorageItem(COOKIES.aiTutorAccessToken, accessToken);
};

export const getAITutorAccessToken = (): string | null => {
	return getLocalStorageItem(COOKIES.aiTutorAccessToken);
};

export const aiTutorAuthTokenRequest = async (): Promise<void> => {
	try {
		const { data }: { data: { access_token: string } } = await processRequest(
			ROUTES_API.AI_TUTOR_AUTH_TOKEN,
			'POST',
		);
		const { access_token } = data || {};
		setAITutorAccessToken(access_token);
	} catch (e) {
		errorHandlerActions.handleError(e);
	}
};

export const refreshAITutorAuthTokenRequest = async (): Promise<boolean> => {
	try {
		const { data }: { data: { access_token: string } } = await processRequest(
			ROUTES_API.AI_TUTOR_REFRESH_AUTH_TOKEN,
			'POST',
		);
		const { access_token } = data || {};
		setAITutorAccessToken(access_token);
		return true;
	} catch (e) {
		errorHandlerActions.handleError(e);
		return false;
	}
};
